import React, { useState, useRef, useEffect } from 'react';
import { Type, Check, Search } from 'lucide-react';

/**
 * Enhanced Font Selector Component
 *
 * Features:
 * - Visual font preview
 * - Search fonts
 * - Popular fonts
 * - Google Fonts integration ready
 * - Font categories (Serif, Sans-Serif, Display, Monospace)
 */
const EnhancedFontSelector = ({
    label,
    value,
    onChange,
    category = 'all' // all, serif, sans-serif, display, monospace
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const pickerRef = useRef(null);

    // Curated font list with categories
    const fonts = [
        // Serif Fonts
        { name: 'IM Fell English', category: 'serif', popular: true },
        { name: 'Playfair Display', category: 'serif', popular: true },
        { name: 'Lora', category: 'serif', popular: true },
        { name: 'Merriweather', category: 'serif', popular: true },
        { name: 'Crimson Text', category: 'serif' },
        { name: 'Libre Baskerville', category: 'serif' },
        { name: 'Cormorant Garamond', category: 'serif' },
        { name: 'Old Standard TT', category: 'serif' },
        { name: 'Vollkorn', category: 'serif' },
        { name: 'Cardo', category: 'serif' },

        // Sans-Serif Fonts
        { name: 'Inter', category: 'sans-serif', popular: true },
        { name: 'Roboto', category: 'sans-serif', popular: true },
        { name: 'Open Sans', category: 'sans-serif', popular: true },
        { name: 'Montserrat', category: 'sans-serif', popular: true },
        { name: 'Poppins', category: 'sans-serif', popular: true },
        { name: 'Raleway', category: 'sans-serif' },
        { name: 'Work Sans', category: 'sans-serif' },
        { name: 'Nunito', category: 'sans-serif' },
        { name: 'Quicksand', category: 'sans-serif' },
        { name: 'Rubik', category: 'sans-serif' },

        // Display Fonts
        { name: 'Cinzel', category: 'display', popular: true },
        { name: 'Bebas Neue', category: 'display' },
        { name: 'Abril Fatface', category: 'display' },
        { name: 'Righteous', category: 'display' },
        { name: 'Lobster', category: 'display' },
        { name: 'Pacifico', category: 'display' },
        { name: 'Dancing Script', category: 'display' },
        { name: 'Great Vibes', category: 'display' },

        // Monospace Fonts
        { name: 'Fira Code', category: 'monospace' },
        { name: 'Source Code Pro', category: 'monospace' },
        { name: 'JetBrains Mono', category: 'monospace' },
        { name: 'Monaco', category: 'monospace' },
        { name: 'Courier New', category: 'monospace' }
    ];

    // Filter fonts based on category and search
    const filteredFonts = fonts.filter(font => {
        const matchesCategory = category === 'all' || font.category === category;
        const matchesSearch = font.name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Popular fonts
    const popularFonts = fonts.filter(f => f.popular);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Handle font selection
    const handleFontSelect = (fontName) => {
        onChange(fontName);
        setIsOpen(false);
        setSearchTerm('');
    };

    // Get category label
    const getCategoryLabel = (cat) => {
        const labels = {
            serif: 'Serif',
            'sans-serif': 'Sans-Serif',
            display: 'Display',
            monospace: 'Monospace'
        };
        return labels[cat] || cat;
    };

    // Group fonts by category
    const groupedFonts = filteredFonts.reduce((acc, font) => {
        if (!acc[font.category]) {
            acc[font.category] = [];
        }
        acc[font.category].push(font);
        return acc;
    }, {});

    return (
        <div className="relative" ref={pickerRef}>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-gray-700)' }}>
                {label}
            </label>

            {/* Font Display Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-3 px-4 py-3 border border-gray-300 hover:border-gray-400 transition-colors"
                style={{
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--color-white)'
                }}
            >
                <Type className="w-5 h-5" style={{ color: 'var(--color-gray-500)' }} />
                <span
                    className="flex-1 text-left text-lg"
                    style={{
                        fontFamily: value || 'Inter',
                        color: 'var(--color-gray-900)'
                    }}
                >
                    {value || 'Select Font'}
                </span>
            </button>

            {/* Font Picker Dropdown */}
            {isOpen && (
                <div
                    className="absolute z-50 mt-2 p-4 bg-white border shadow-lg"
                    style={{
                        borderRadius: 'var(--radius-md)',
                        width: '380px',
                        maxHeight: '500px',
                        overflowY: 'auto'
                    }}
                >
                    {/* Search Input */}
                    <div className="mb-4">
                        <div className="relative">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                                style={{ color: 'var(--color-gray-400)' }}
                            />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search fonts..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:border-primary focus:outline-none"
                                style={{ borderRadius: 'var(--radius-sm)' }}
                            />
                        </div>
                    </div>

                    {/* Popular Fonts (if no search) */}
                    {!searchTerm && (
                        <div className="mb-4">
                            <h4 className="text-xs font-semibold mb-2 px-2" style={{ color: 'var(--color-gray-700)' }}>
                                Popular Fonts
                            </h4>
                            <div className="space-y-1">
                                {popularFonts.map((font, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => handleFontSelect(font.name)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100 transition-colors text-left"
                                        style={{
                                            borderRadius: 'var(--radius-sm)',
                                            backgroundColor: value === font.name ? 'var(--color-accent-light)' : 'transparent'
                                        }}
                                    >
                                        <div className="flex-1">
                                            <div
                                                className="text-base"
                                                style={{
                                                    fontFamily: font.name,
                                                    color: 'var(--color-gray-900)'
                                                }}
                                            >
                                                {font.name}
                                            </div>
                                            <div className="text-xs" style={{ color: 'var(--color-gray-500)' }}>
                                                {getCategoryLabel(font.category)}
                                            </div>
                                        </div>
                                        {value === font.name && (
                                            <Check className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* All Fonts Grouped by Category */}
                    <div>
                        <h4 className="text-xs font-semibold mb-2 px-2" style={{ color: 'var(--color-gray-700)' }}>
                            {searchTerm ? 'Search Results' : 'All Fonts'}
                        </h4>

                        {Object.keys(groupedFonts).length === 0 && (
                            <div className="text-center py-8" style={{ color: 'var(--color-gray-500)' }}>
                                No fonts found
                            </div>
                        )}

                        {Object.entries(groupedFonts).map(([cat, fontList]) => (
                            <div key={cat} className="mb-3">
                                {!searchTerm && (
                                    <h5 className="text-xs font-medium mb-1 px-2" style={{ color: 'var(--color-gray-600)' }}>
                                        {getCategoryLabel(cat)}
                                    </h5>
                                )}
                                <div className="space-y-1">
                                    {fontList.map((font, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => handleFontSelect(font.name)}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100 transition-colors text-left"
                                            style={{
                                                borderRadius: 'var(--radius-sm)',
                                                backgroundColor: value === font.name ? 'var(--color-accent-light)' : 'transparent'
                                            }}
                                        >
                                            <div className="flex-1">
                                                <div
                                                    className="text-base"
                                                    style={{
                                                        fontFamily: font.name,
                                                        color: 'var(--color-gray-900)'
                                                    }}
                                                >
                                                    {font.name}
                                                </div>
                                            </div>
                                            {value === font.name && (
                                                <Check className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedFontSelector;
